import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Bookmark, FileText, Menu } from "lucide-react";
import VerifiedOrgMark from "./VerifiedOrgMark";
import SocialUserAvatar from "@/components/avatars/SocialUserAvatar";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { API_BASE_URL } from "@/lib/apiBase";
import { useSocialPaths } from "@/lib/socialPaths";
import { useSocialProfile } from "@/scripts/SocialProfileContext";

/**
 * Phone/tablet shortcuts for Circa Social Home (Saved / Drafts / communities).
 * Desktop uses SocialHomeLeftRail (xl+).
 */
export default function SocialHomeMobileMore({ accessToken }) {
  const paths = useSocialPaths();
  const { profile, complete } = useSocialProfile();
  const [open, setOpen] = useState(false);
  const [mine, setMine] = useState([]);
  const [loadingMine, setLoadingMine] = useState(false);

  const loadMine = useCallback(async () => {
    if (!accessToken) return;
    setLoadingMine(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/social/communities/mine`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: { limit: 40 },
      });
      setMine(res.data?.communities || []);
    } catch (err) {
      console.error("Failed to load Your communities:", err);
      setMine([]);
    } finally {
      setLoadingMine(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (open) loadMine();
  }, [open, loadMine]);

  const handle = profile?.username;
  const profileHref = handle ? paths.user(handle) : paths.profileEdit();
  const savedHref = handle
    ? `${paths.user(handle)}?tab=saved`
    : paths.profileEdit();
  const draftsHref = handle
    ? `${paths.user(handle)}?tab=drafts`
    : paths.profileEdit();

  return (
    <div className="xl:hidden flex items-center justify-between gap-2">
      <p className="text-sm font-medium text-foreground">Circa Social</p>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-10 gap-2"
            aria-label="Social shortcuts"
          >
            <Menu className="h-4 w-4" />
            More
          </Button>
        </SheetTrigger>
        <SheetContent side="bottom" className="max-h-[85dvh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Shortcuts</SheetTitle>
          </SheetHeader>
          <div className="mt-4 space-y-6 pb-6">
            {complete && profile ? (
              <Link
                to={profileHref}
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 min-w-0 rounded-md p-2 -mx-1 hover:bg-muted/50 transition-colors"
              >
                <SocialUserAvatar
                  user={profile}
                  avatarUrl={profile.avatar_url}
                  displayName={profile.display_name}
                  className="h-10 w-10 shrink-0"
                  fallbackClassName="bg-primary/10 text-primary text-xs"
                />
                <div className="min-w-0 flex-1">
                  <p
                    className="text-sm font-semibold truncate"
                    title={profile.display_name || "You"}
                  >
                    {profile.display_name || "You"}
                  </p>
                  {handle ? (
                    <p
                      className="text-xs text-muted-foreground truncate"
                      title={`@${handle}`}
                    >
                      @{handle}
                    </p>
                  ) : null}
                </div>
              </Link>
            ) : null}

            <nav className="space-y-1">
              <Link
                to={savedHref}
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 rounded-md px-2 py-3 text-sm hover:bg-muted/50 transition-colors min-h-11"
              >
                <Bookmark className="h-4 w-4 shrink-0 text-muted-foreground" />
                Saved
              </Link>
              <Link
                to={draftsHref}
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 rounded-md px-2 py-3 text-sm hover:bg-muted/50 transition-colors min-h-11"
              >
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                Drafts
              </Link>
              <Link
                to="/explore"
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 rounded-md px-2 py-3 text-sm hover:bg-muted/50 transition-colors min-h-11"
              >
                Explore
              </Link>
            </nav>

            <section className="space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground px-1">
                Your communities
              </h3>
              {loadingMine ? (
                <p className="text-sm text-muted-foreground px-1">Loading…</p>
              ) : mine.length === 0 ? (
                <p className="text-sm text-muted-foreground px-1">
                  No communities yet. Find some on Explore.
                </p>
              ) : (
                <ul className="space-y-1">
                  {mine.map((c) => (
                    <li key={c.community_id} className="min-w-0">
                      <Link
                        to={paths.community(c.slug || c.community_id)}
                        onClick={() => setOpen(false)}
                        className="flex items-center gap-1 min-w-0 max-w-full px-2 py-3 text-sm hover:bg-muted/50 rounded-md min-h-11"
                      >
                        <span className="truncate" title={c.display_name}>
                          {c.display_name}
                        </span>
                        <VerifiedOrgMark
                          org={c}
                          title="Verified community"
                          className="h-3 w-3 shrink-0"
                        />
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
